import { forwardRef } from 'react';
import { Shipment, STATUS_CONFIG } from '../../../mocks/shipments';

interface Props {
  shipment: Shipment;
}

const ShipmentReceipt = forwardRef<HTMLDivElement, Props>(({ shipment }, ref) => {
  const cfg = STATUS_CONFIG[shipment.status];
  const issuedAt = new Date().toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const progressMap: Record<string, number> = {
    hold: 15, picked_up: 25, in_transit: 55, out_for_delivery: 82, delivered: 100,
  };

  return (
    <div ref={ref} className="receipt-content" style={{ display: 'none', fontFamily: 'Georgia, serif' }}>
      <style>{`
        @media print {
          .receipt-content {
            display: block !important;
            padding: 32px;
            max-width: 760px;
            margin: 0 auto;
            background: white;
            color: #111;
          }
          .receipt-header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            margin-bottom: 24px;
            padding-bottom: 20px;
            border-bottom: 2px solid #111;
          }
          .receipt-company-name {
            font-size: 24px;
            font-weight: 700;
            color: #0d9488;
            letter-spacing: -0.5px;
          }
          .receipt-title {
            font-size: 13px;
            color: #6b7280;
            margin-top: 2px;
          }
          .receipt-meta {
            text-align: right;
            font-size: 11px;
            color: #6b7280;
            line-height: 1.8;
          }
          .receipt-tracking-id {
            font-family: 'Courier New', monospace;
            font-size: 18px;
            font-weight: 700;
            color: #111;
            letter-spacing: 1px;
          }
          .receipt-barcode {
            font-family: 'Courier New', monospace;
            font-size: 32px;
            letter-spacing: 2px;
            color: #111;
            line-height: 1;
            margin: 8px 0 4px;
          }
          .receipt-status-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 12px;
            border-radius: 99px;
            border: 1.5px solid;
            font-size: 11px;
            font-weight: 600;
            margin-top: 4px;
          }
          .receipt-section-title {
            font-size: 10px;
            font-weight: 700;
            color: #9ca3af;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
          }
          .receipt-grid-2 {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 24px;
            margin: 20px 0;
          }
          .receipt-box {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 14px;
          }
          .receipt-party-name {
            font-size: 14px;
            font-weight: 700;
            color: #111;
          }
          .receipt-party-sub {
            font-size: 11px;
            color: #6b7280;
            margin-top: 2px;
          }
          .receipt-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
            margin: 16px 0;
          }
          .receipt-table th {
            text-align: left;
            padding: 6px 10px;
            background: #f9fafb;
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #6b7280;
            border: 1px solid #e5e7eb;
          }
          .receipt-table td {
            padding: 8px 10px;
            border: 1px solid #e5e7eb;
            color: #111;
            font-size: 12px;
          }
          .receipt-timeline-item {
            display: flex;
            gap: 10px;
            padding: 8px 0;
            border-bottom: 1px solid #f3f4f6;
          }
          .receipt-timeline-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #0d9488;
            flex-shrink: 0;
            margin-top: 4px;
          }
          .receipt-progress-bar {
            height: 6px;
            background: #e5e7eb;
            border-radius: 99px;
            margin: 8px 0;
            overflow: hidden;
          }
          .receipt-progress-fill {
            height: 100%;
            background: #0d9488;
            border-radius: 99px;
          }
          .receipt-footer {
            margin-top: 28px;
            padding-top: 16px;
            border-top: 1px solid #e5e7eb;
            font-size: 10px;
            color: #9ca3af;
            text-align: center;
          }
          .receipt-note-box {
            background: #fffbeb;
            border: 1px solid #fde68a;
            border-radius: 8px;
            padding: 10px 14px;
            font-size: 11px;
            color: #92400e;
            margin: 12px 0;
          }
        }
      `}</style>

      {/* Header */}
      <div className="receipt-header">
        <div>
          <div className="receipt-company-name">SwiftTrack</div>
          <div className="receipt-title">Global Logistics &amp; Courier Services</div>
          <div style={{ marginTop: '14px' }}>
            <div className="receipt-barcode">
              {'|'.repeat(18)}&nbsp;{'||'.repeat(6)}&nbsp;{'|'.repeat(12)}&nbsp;{'|||'.repeat(4)}
            </div>
            <div className="receipt-tracking-id">{shipment.id}</div>
            <div
              className="receipt-status-badge"
              style={{
                borderColor: shipment.status === 'hold' ? '#ef4444' : shipment.status === 'delivered' ? '#16a34a' : '#0d9488',
                color: shipment.status === 'hold' ? '#ef4444' : shipment.status === 'delivered' ? '#16a34a' : '#0d9488',
              }}
            >
              {cfg.label}
            </div>
          </div>
        </div>
        <div className="receipt-meta">
          <div style={{ fontWeight: 700, color: '#111', marginBottom: '4px', fontSize: '13px' }}>
            SHIPMENT RECEIPT
          </div>
          <div>Issued: {issuedAt}</div>
          <div>Service: {shipment.service}</div>
          <div>Carrier: {shipment.carrier}</div>
          <div style={{ marginTop: '8px', fontWeight: 600, color: '#0d9488' }}>
            Est. Delivery: {shipment.estimatedDelivery}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div>
        <div className="receipt-section-title">Delivery Progress</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#6b7280', marginBottom: '4px' }}>
          <span>Picked Up</span><span>In Transit</span><span>Out for Delivery</span><span>Delivered</span>
        </div>
        <div className="receipt-progress-bar">
          <div className="receipt-progress-fill" style={{ width: `${progressMap[shipment.status]}%` }} />
        </div>
        <div style={{ textAlign: 'right', fontSize: '10px', color: '#6b7280' }}>{progressMap[shipment.status]}% complete</div>
      </div>

      {/* Parties */}
      <div className="receipt-grid-2">
        <div className="receipt-box">
          <div className="receipt-section-title">Sender (From)</div>
          <div className="receipt-party-name">{shipment.sender.name}</div>
          <div className="receipt-party-sub">{shipment.sender.address}</div>
          <div className="receipt-party-sub">{shipment.sender.city}, {shipment.sender.country}</div>
        </div>
        <div className="receipt-box">
          <div className="receipt-section-title">Recipient (To)</div>
          <div className="receipt-party-name">{shipment.receiver.name}</div>
          <div className="receipt-party-sub">{shipment.receiver.address}</div>
          <div className="receipt-party-sub">{shipment.receiver.city}, {shipment.receiver.country}</div>
        </div>
      </div>

      {/* Package details */}
      <div className="receipt-section-title">Package Details</div>
      <table className="receipt-table">
        <thead>
          <tr>
            <th>Contents</th>
            <th>Weight</th>
            <th>Dimensions</th>
            <th>Declared Value</th>
            <th>Payment</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{shipment.description}</td>
            <td>{shipment.weight} kg</td>
            <td>{shipment.dimensions}</td>
            <td>${shipment.value.toLocaleString()}</td>
            <td>{shipment.paymentMethod ?? 'N/A'}</td>
          </tr>
        </tbody>
      </table>

      {/* Admin note */}
      {shipment.note && (
        <div className="receipt-note-box">
          <strong>Important Notice:</strong> {shipment.note}
        </div>
      )}

      {/* Timeline */}
      <div style={{ marginTop: '16px' }}>
        <div className="receipt-section-title">Tracking History</div>
        <div className="receipt-box">
          {[...shipment.timeline].reverse().map((evt, i) => (
            <div key={i} className="receipt-timeline-item">
              <div className="receipt-timeline-dot" style={{
                background: evt.status === 'hold' ? '#ef4444' : evt.status === 'delivered' ? '#16a34a' : evt.status === 'out_for_delivery' ? '#22c55e' : '#0d9488'
              }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: '11px', color: '#111' }}>
                  {STATUS_CONFIG[evt.status].label} — {evt.location}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>{evt.description}</div>
                <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{evt.timestamp}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="receipt-footer">
        <p>SwiftTrack Global Logistics · support@swifttrack.com · 1-800-SWIFT-00</p>
        <p style={{ marginTop: '4px' }}>
          This receipt is auto-generated. For disputes or inquiries, visit swifttrack.com/contact with tracking ID {shipment.id}
        </p>
        <p style={{ marginTop: '4px' }}>
          Printed: {new Date().toLocaleString()}
        </p>
      </div>
    </div>
  );
});

ShipmentReceipt.displayName = 'ShipmentReceipt';
export default ShipmentReceipt;
